import { getStoredToken } from './auth'
import { multipartRequest } from './http'
import type { CollateralType, LifeCycleStatus } from '@/views/debt/debt.types'
import type { ApplicationStatus, ApplicationType } from '@/services/application/application.types'
import type { DistributorKycItem } from '@/services/kyc/kyc.types'

const API_BASE_URL = import.meta.env.VITE_DAPI_URL || ''

export interface ContactDetails {
  email?: string
  phone?: string
  street?: string
  streetNumber?: string
  city?: string
  zip?: string
  country?: string
}

interface CollateralSummary {
  type: CollateralType
  street: string
  streetNo?: string
}

export type ApplicationCollateral = CollateralSummary

export interface ApplicationSummary {
  id: string
  type: ApplicationType
  status: ApplicationStatus
  collateralProperty?: ApplicationCollateral
  submittedAt?: string
}

export interface DebtCollateral extends CollateralSummary {
  apartmentNumber?: string
  apartmentNumberBrf?: string
  housingAssociation?: string
  municipality?: { name: string; code: string }
  propertyLabel?: string
}

export interface DebtSummary {
  id: string
  hasBridgeLoan: boolean
  status: LifeCycleStatus
  collateralProperty: DebtCollateral
  startDate: string
}

export interface DepositLoanSummary {
  id: string
  status: LifeCycleStatus
  collateralProperty: DebtCollateral
  startDate: string | null
}

/**
 * The relation the organisation reaches a user through. Each value maps to its own
 * list endpoint, see `userRelationPaths`.
 */
export type UserRelation = 'customers' | 'applicants' | 'debtors'

export interface UserListItem {
  userId: string
  personalNumber: string
  givenName: string | null
  surname: string | null
}

export interface UserDetails {
  userId: string
  givenName: string
  surname: string
  personalNumber: string
  contactDetails: ContactDetails
  applications: ApplicationSummary[]
  debts: DebtSummary[]
  depositLoans: DepositLoanSummary[]
  /** The customer's KYC cycles with this distributor. */
  kycs: DistributorKycItem[]
}

const getAuthorizationHeader = () => {
  const token = getStoredToken()

  if (!token || !('jwt' in token) || !token.jwt) {
    throw new Error('Missing authentication token.')
  }

  return { authorization: `bearer ${token.jwt}` }
}

// `customers` lists at the collection root, so the path can't be built by appending the relation.
const userRelationPaths: Record<UserRelation, string> = {
  customers: '/borgo/distributor/customers',
  applicants: '/borgo/distributor/customers/applicants',
  debtors: '/borgo/distributor/customers/debtors',
}

const userRelationErrorMessages: Record<UserRelation, string> = {
  customers: 'Kunde inte hämta kunder',
  applicants: 'Kunde inte hämta sökande',
  debtors: 'Kunde inte hämta låntagare',
}

/**
 * Lists the users the organisation can access through the given relation.
 * The endpoint returns the complete list — filtering by name or personal number is
 * done server-side through `search`, everything else (paging, sorting) is client-side.
 */
export const fetchUsersByRelation = async (relation: UserRelation, search?: string): Promise<UserListItem[]> => {
  const trimmedSearch = search?.trim()
  const queryString = trimmedSearch ? `?${new URLSearchParams({ search: trimmedSearch }).toString()}` : ''

  const response = await fetch(`${API_BASE_URL}${userRelationPaths[relation]}${queryString}`, {
    method: 'GET',
    credentials: 'include',
    headers: {
      'content-type': 'application/json',
      ...getAuthorizationHeader(),
    },
  })

  if (!response.ok) {
    throw new Error(userRelationErrorMessages[relation])
  }

  return await response.json()
}

export interface UserSearchResultItem extends UserListItem {
  /** Every relation the search matched this user through - the same person can be e.g. both an applicant and a customer. */
  relations: UserRelation[]
}

const ALL_USER_RELATIONS: UserRelation[] = ['applicants', 'debtors', 'customers']

/**
 * Searches every relation at once and merges the results by user, since there's no single
 * endpoint listing all of them together.
 */
export const searchUsers = async (search: string): Promise<UserSearchResultItem[]> => {
  const settled = await Promise.allSettled(
    ALL_USER_RELATIONS.map(async (relation) => ({ relation, items: await fetchUsersByRelation(relation, search) })),
  )

  if (settled.some((result) => result.status === 'rejected')) {
    const firstRejection = settled.find((result): result is PromiseRejectedResult => result.status === 'rejected')
    if (firstRejection) {
      throw firstRejection.reason instanceof Error ? firstRejection.reason : new Error('Kunde inte hämta användare')
    }
  }

  const fulfilled = settled.filter(
    (result): result is PromiseFulfilledResult<{ relation: UserRelation; items: UserListItem[] }> =>
      result.status === 'fulfilled',
  )

  const merged = new Map<string, UserSearchResultItem>()

  for (const { relation, items } of fulfilled.map((result) => result.value)) {
    for (const item of items) {
      const existing = merged.get(item.userId)
      if (existing) {
        existing.relations.push(relation)
      } else {
        merged.set(item.userId, { ...item, relations: [relation] })
      }
    }
  }

  return Array.from(merged.values())
}

export const fetchUserById = async (userId: string): Promise<UserDetails> => {
  const response = await fetch(`${API_BASE_URL}/borgo/distributor/customers/${encodeURIComponent(userId)}`, {
    method: 'GET',
    credentials: 'include',
    headers: {
      'content-type': 'application/json',
      ...getAuthorizationHeader(),
    },
  })

  if (!response.ok) {
    throw new Error(`Kunde inte hämta kund med id ${userId}`)
  }

  return await response.json()
}

export const uploadUserDocument = async (
  userId: string,
  payload: { file: File; type: string },
): Promise<{ documentId: string }> => {
  const formData = new FormData()
  formData.append('file', payload.file)
  formData.append('type', payload.type)

  return await multipartRequest<{ documentId: string }>(
    `/borgo/distributor/customers/${encodeURIComponent(userId)}/documents`,
    'POST',
    formData,
  )
}
